/**
 * Maps — a first-class page beside Events/Chats/Find (the Slopes model: a
 * styled map OF the venue with your group on it, not a generic atlas).
 *
 * Two screens share this component (design hand-off "Map page — event picker
 * becomes an index screen", 2026-09-14):
 *   /map            — the INDEX: every event of yours with a published map,
 *                     Upcoming then Past, in the RPC's own order. An ordinary
 *                     page with a header; tapping a row pushes the map.
 *   /map/:eventId   — the MAP. Google Maps' grammar on Prii's surface: the map
 *                     fills the screen, a floating search pill sits at the top,
 *                     the layer chips ride under it, anything selected takes a
 *                     floating row at the bottom. Nothing with words lands on
 *                     the map itself. Reached from the index it carries a back
 *                     button to the left of the search pill; reached as a deep
 *                     link (browser QA, shared links) it does not — there is no
 *                     index behind it.
 *
 * Choosing a map never touches the Find group: pucks are drawn only while the
 * selected group's event IS this map's event; otherwise the map renders alone.
 */

import { usePageLoading } from './motion/PetalLoader';
import { motionFlags } from '../lib/motionFlags';
import MapLoader from './motion/MapLoader';
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useIsRestoring, useQueryClient } from '@tanstack/react-query';
import {
  isFestivalNotOver,
  isFestivalOnNow,
  refreshFestivalIfChanged,
  setMapOnScreen,
} from '../lib/festivalOffline';
import { isInPresenceWindow } from '../utils/eventCutoff';
import { ChevronLeft, Navigation, Search, X } from 'lucide-react';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useFindProximity } from '../contexts/FindProximityContext';
import { useFestivalMap } from '../hooks/queries/useFestivalMap';
import { NATIVE_OR_SIM } from '../lib/proximity/native/simPhone';
import { useMyFestivalMaps } from '../hooks/queries/useMyFestivalMaps';
import { useUpcomingFestivalMaps } from '../hooks/queries/useUpcomingFestivalMaps';
import NothingYoursYet from './ui/NothingYoursYet';
import { useCancelledEvents } from '../hooks/queries/useCancelledEvents';
import { useEventLineup } from '../hooks/queries/useEventLineup';
import { useEventDetail } from '../hooks/queries/useEventDetail';
import { appLog } from '../lib/appLog';
import { useSearchLog } from '../hooks/useSearchLog';
import { parseFestivalMap, KIND_DISPLAY, venueBounds } from '../lib/map/festivalMapGeoJson';
import { slotKey } from '../lib/map/kinds';
import { haversineMeters } from '../lib/proximity/engine/geodesy';
import { leftAsItWas, rememberLeave } from '../lib/map/mapReturn';
import {
  combineHorizontalAccuracyMeters,
  formatFindDistance,
  formatFindDistanceWithWalkTime,
} from '../lib/proximity/engine/findDistanceFormat';
import { iconMarkup } from '../lib/map/mapIcons';
import Avatar from './Avatar';
import {
  scenarioNameFromSearch,
  scenarioFriendNameFromSearch,
  buildMapScenario,
} from '../lib/map/mapScenarios';
import { playRequested, createPlayground } from '../lib/map/playground';
import MapPlayground from './find/MapPlayground';
import { buildPeerPositions, isPositionExpired, positionAgeTone } from '../lib/map/peerPositions';
import { formatStalenessShort } from '../lib/proximity/engine/staleness';
import { bleNearbyLabel } from '../lib/proximity/engine/peerDisplay';
import { Highlight } from './find/FindWhoPicker';
import { formatEventDate } from '../utils/eventDateFormat';
import { writeStoredMapEventId } from '../lib/mapSelectedEventStorage';
import FestivalDownloadPill from './FestivalDownloadPill';
import IndexTile from './IndexTile';
import { featureDisplayName } from '../lib/map/featureName';
import useMapSelfLocation from '../hooks/useMapSelfLocation';
import PermissionsStatus, { permissionRows } from './find/PermissionsStatus';
import { mapTitle } from '../lib/map/mapTitle';
import StageSetsPreview from './find/StageSetsPreview';
import { FIND_OFFER_M, findOffered } from '../lib/proximity/findRange';
import { goBackTo } from '../lib/navHistory';
import { markMapTap } from '../lib/map/openTiming';
import { isWelcomeDemo } from '../lib/welcomeDemo';
import useDeviceHeading from '../hooks/useDeviceHeading';

const FestivalMap = lazy(() => import('./find/FestivalMap'));
// The three.js map, for editions that carry a scene from the festival_map_3d pipeline (terrain.scene3d).
const FestivalMap3D = lazy(() => import('./find/FestivalMap3D'));

const NO_ROWS = [];
/** Walking pace behind the ETA. 1.4 m/s is an unhurried festival walk. */
/**
 * Guidance ends itself here rather than waiting to be dismissed - but not early. 18 m cut
 * the directions off while you were still walking (operator, 2026-09-24). GPS is all we have
 * for a place (a peer gets UWB and BLE on top), so this is about as tight as it can be
 * honestly drawn.
 */
const ARRIVED_M = 7;
/** Walking to a friend: both of you are on GPS, so "there" is looser than for a place. */
const ARRIVED_PERSON_M = 12;
/** A place with a footprint (a stage, a tent) is reached at its edge, not its middle: its anchor
 *  sits inside the fence where nobody can stand (2026-09-25 audit). Capped so a huge lawn does
 *  not end guidance from across the field. */
const ARRIVED_EDGE_M = 10;
const ARRIVED_MAX_M = 60;
/** One GPS wobble inside the radius is not arriving: it has to hold this long. */
const ARRIVE_HOLD_MS = 3000;
/** Stopped near the place for this long counts as there, even if the fix never gets inside
 *  the radius - a crowd in front of a stage stands still (operator, 2026-09-25). */
const DWELL_NEAR_M = 25;
const DWELL_STILL_M = 8;
const DWELL_MS = 45000;
/** One bad fix outside the venue is not leaving it: off site has to hold this long. */
const OFF_SITE_HOLD_MS = 20000;
/** How often the page re-reads positions while a person is selected or guidance runs. */
const LIVE_TICK_MS = 2000;

/** How close counts as arrived for a guide target. */
function arriveRadius(guide) {
  if (guide?.peerId) return ARRIVED_PERSON_M;
  const edge =
    Number.isFinite(guide?.extentM) && guide.extentM > 0 ? guide.extentM + ARRIVED_EDGE_M : 0;
  return Math.min(ARRIVED_MAX_M, Math.max(ARRIVED_M, edge));
}
/** Local calendar date, YYYY-MM-DD, `daysAgo` days back. */
function localDay(daysAgo = 0) {
  const d = new Date(Date.now() - daysAgo * 86400000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** ~250 m: the margin around the venue that still counts as being at the festival (FestivalMap3D's clamp). */
const VENUE_MARGIN_DEG = 0.0025;

/** Compass bearing from a to b, degrees clockwise from north. */
function bearingDeg(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(toRad(b.lat));
  const x =
    Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}
// Scene types search skips: scenery, context drawn by rule, and what the feature list already answers.
const SCENE_UNSEARCHABLE = new Set([
  'tree',
  'palm',
  'pool',
  'fountain',
  'road',
  'path',
  'walkway',
  'route',
  'ground',
  'icon',
  'park',
  'ocean',
  'beach',
  'pier',
  'lot',
  'roundabout',
  'fence',
  'door',
  'seating',
  'lifeguard',
]);
// A scene feature without a kind takes the vocabulary row that names what it is, for the result's icon and label.
const SCENE_KIND = {
  wheel: 'ride',
  art: 'art',
  dolab: 'art',
  canopy: 'art',
  mainstage: 'stage',
  gate: 'entrance',
};
const getNoPeers = () => NO_ROWS;
const noSubscribe = () => () => {};

export default function MapsPage() {
  const { eventId: routeEventId } = useParams();
  if (!routeEventId) return <MapsIndex />;
  return <MapScreen eventId={routeEventId} />;
}

/** 2a — the index: YOUR MAPS (the upcoming and current events you are going to or in a group for, by event, soonest
 * first), then MORE MAPS - every other public one (Sam, 2026-10-26: the page was bare for anyone new). More maps comes
 * in two tiers: a venue with several shows is one row (Hollywood Bowl) that opens the venue's map - the same map every
 * night, so no list of shows (Sam, 2026-10-26) - and a one-off event (a festival) is its own row. Only yours download; a browsed map is kept only among the map store's recent few
 * (lib/map/mapStore MAX_EDITIONS). Expired and cancelled events are never listed. */
function MapsIndex() {
  const navigate = useNavigate();
  const { session, authReady } = useCurrentUserProfile();
  const userId = session?.user?.id ?? null;
  const { data: mine, isLoading: mineLoading } = useMyFestivalMaps(userId);
  const { data: upcoming, isLoading: upcomingLoading } = useUpcomingFestivalMaps();
  // list_upcoming_festival_maps joins events but does not look at status, so a cancelled
  // festival keeps its published map on the index (Ocean Way, 2026-09-22).
  const cancelled = useCancelledEvents();
  const [query, setQuery] = useState('');
  useSearchLog('maps', query);
  const location = useLocation();

  const open = useCallback(
    (eventId) => {
      writeStoredMapEventId(userId, eventId);
      markMapTap();
      navigate(`/map/${encodeURIComponent(eventId)}`);
    },
    [navigate, userId],
  );

  const now = Date.now();
  // Listed until the festival is over (ends_at: 6 AM after its last day), not until its start time: Portola left
  // this list at noon on day one.
  const isUpcoming = (m) => !cancelled.has(m.event_id) && isFestivalNotOver(m, now);
  const q = query.trim().toLowerCase();
  const matches = (m) =>
    !q ||
    (m.event_name || '').toLowerCase().includes(q) ||
    (m.venue_name || '').toLowerCase().includes(q);
  const yours = (mine ?? []).filter(isUpcoming).filter(matches); // RPC order: next event first
  const mineIds = new Set((mine ?? []).map((m) => m.event_id));
  const others = (upcoming ?? []).filter((m) => !mineIds.has(m.event_id)).filter(isUpcoming);
  // Two tiers: a venue with several shows is one row; a one-off event keeps its own. Order: each by its next date.
  const byVenue = new Map();
  for (const m of others) {
    const k = m.venue_name || m.event_id;
    byVenue.set(k, [...(byVenue.get(k) ?? []), m]);
  }
  // A venue you have a show at opens YOUR show (Sam, 2026-10-28): its map is the same drawing, but only your event's
  // map puts you and your group on it - the venue's next other show had neither.
  const myShowAt = new Map();
  for (const m of (mine ?? []).filter(isUpcoming))
    if (m.venue_name && !myShowAt.has(m.venue_name)) myShowAt.set(m.venue_name, m);
  const more = [...byVenue.entries()]
    .map(([name, shows]) =>
      shows.length > 1 || myShowAt.has(name)
        ? { venue: name, shows, next: myShowAt.get(name) ?? shows[0] }
        : { event: shows[0], next: shows[0] },
    )
    .filter(
      (r) =>
        !q ||
        (r.venue ?? '').toLowerCase().includes(q) ||
        r.shows?.some(matches) ||
        (r.event && matches(r.event)),
    );
  const hasAny = (mine ?? []).some(isUpcoming) || others.length > 0; // nothing at all: no search bar over an empty page
  // While the saved cache restores (up to 5 s at launch) queries report idle, not loading: without this the page said
  // "Find events" to someone going to three festivals, then swapped to the list (2026-10-12 audit).
  const restoring = useIsRestoring();
  const loading = restoring || !authReady || (userId && mineLoading) || upcomingLoading;
  const pageLoad = usePageLoading(Boolean(loading));

  // On a festival day, Maps opens on that festival (operator, 2026-09-23). Backing out to this list says stay.
  const onNow =
    session && !q
      ? (mine ?? []).filter((m) => !cancelled.has(m.event_id) && isFestivalOnNow(m))
      : [];
  const jumpTo = !location.state?.stay && onNow.length === 1 ? onNow[0].event_id : null;

  // Tiles, two across, in the Events feed's style (Sam's A, 2026-10-28). The photo is the map's own cover - a still of
  // our 3D map (migration 166, scripts/render_map_covers.mjs) - else the event's image.
  const shows = (n) => `${n} show${n === 1 ? '' : 's'}`;
  const when = (m) => formatEventDate({ event_datetime: m.event_datetime, timezone: m.timezone });
  const tile = (key, photo, name, meta, onOpen, corner = null) => (
    <IndexTile key={key} photo={photo} name={name} meta={meta} onOpen={onOpen} corner={corner} />
  );
  // Only yours download (Sam, 2026-10-26: browsed maps are not saved for later).
  const yourTile = (m) =>
    tile(
      m.event_id,
      m.cover_url,
      m.event_name,
      [when(m), m.venue_name].filter(Boolean).join(' · '),
      () => open(m.event_id),
      <FestivalDownloadPill eventId={m.event_id} eventName={m.event_name} userId={userId} tile />,
    );
  // A venue tile is the venue, not a show: its next show's cover is the venue's map.
  const moreTile = (r) =>
    r.venue
      ? tile(
          `venue:${r.venue}`,
          r.next.cover_url ?? r.shows[0].cover_url,
          r.venue,
          `${shows(r.shows.length)} · next ${when(r.shows[0])}`,
          () => open(r.next.event_id),
        )
      : tile(
          r.event.event_id,
          r.event.cover_url,
          r.event.event_name,
          [when(r.event), r.event.venue_name].filter(Boolean).join(' · '),
          () => open(r.event.event_id),
        );

  if (jumpTo) return <Navigate to={`/map/${encodeURIComponent(jumpTo)}`} replace />;
  if (pageLoad.show) return pageLoad.node; // the whole page at once while nothing is saved (Sam, 2026-10-20)

  return (
    <div className="list-index">
      <h1 className="sr-only">Maps</h1>
      {(hasAny || query) && (
        <div className="maps-page__search list-index__search">
          <Search size={15} aria-hidden="true" />
          <input
            type="search"
            value={query}
            placeholder="Search maps"
            aria-label="Search maps"
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button type="button" aria-label="Clear search" onClick={() => setQuery('')}>
              <X size={14} />
            </button>
          )}
        </div>
      )}
      {yours.length === 0 && more.length === 0 ? (
        q ? (
          <p className="list-index__note">No maps match.</p>
        ) : (
          <NothingYoursYet text="Maps show up for events you're going to." />
        )
      ) : (
        <>
          {yours.length > 0 && (
            <>
              <h2 className="list-index__label">Your maps</h2>
              <div className="event-tiles index-tiles">{yours.map(yourTile)}</div>
            </>
          )}
          {more.length > 0 && (
            <>
              <h2 className="list-index__label">More maps</h2>
              <div className="event-tiles index-tiles">{more.map(moreTile)}</div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** 2D/3D per festival, this app session. */
const viewModeByEvent = new Map();

/** 2b — the map, with a way back when the index is behind it. */
function MapScreen({ eventId }) {
  const navigate = useNavigate();
  // Opening a map with signal checks for a newer version (cheap; re-downloads only on change). No signal: the copy
  // on the phone shows, as before.
  const refreshClient = useQueryClient();
  const { session: refreshSession } = useCurrentUserProfile();
  useEffect(() => {
    void refreshFestivalIfChanged(refreshClient, eventId, refreshSession?.user?.id ?? null);
  }, [refreshClient, eventId, refreshSession?.user?.id]);
  const location = useLocation();
  const proximity = useFindProximity();
  // Pucks only while the selected group's event IS this map: looking at a map never touches the Find group.
  // Dev only: ?scenario=<name> stands in a friend in a known close-range state (lib/map/mapScenarios.js).
  const scenarioName = scenarioNameFromSearch(location.search);
  // Dev only: ?play=1 is the playground - you and fake friends moving, driven from the keyboard (lib/map/playground.js).
  const playMode = playRequested(location.search);
  // Declared before mapRootId reads it: below it, every map threw a TDZ ReferenceError (2026-10-04).
  const { data: event } = useEventDetail(eventId);
  // Live when the session's event is this map's, by id or by festival family (a group on a day child, the map
  // on the parent, or the reverse - 2026-10-04 audit).
  const mapRootId = event?.festival_parent_event_id ?? eventId;
  const live =
    eventId != null &&
    (eventId === proximity.sessionEventId ||
      (proximity.sessionEventId != null &&
        mapRootId === (proximity.sessionEventRootId ?? proximity.sessionEventId)) ||
      scenarioName != null ||
      playMode);

  const { data: mapPayload, isLoading } = useFestivalMap(eventId);
  // Loading the map (Stage 4): its real steps - data, the 3D code, the first frame - for MapLoader, when a variant is on.
  const { mapload: mapLoaderVariant, hold: loaderHoldMs } = motionFlags();
  // The steps belong to the map they were reported for. A reset in an effect lost them: the 3D map's effects run before
  // this page's, so with a downloaded map it reported mounted/ready first and the reset wiped both - the loader then sat
  // at two thirds, or came back over a map already drawn (Sam, 2026-10-20).
  const [mapStageRaw, setMapStage] = useState({ eventId: null, mounted: false, ready: false });
  const mapStage = mapStageRaw.eventId === eventId ? mapStageRaw : { mounted: false, ready: false };
  const eventIdRef = useRef(eventId);
  eventIdRef.current = eventId;
  const [loaderGone, setLoaderGone] = useState(false);
  const [loaderHeld, setLoaderHeld] = useState(loaderHoldMs > 0);
  // Shown only if the map is still loading after a moment: a map already built (the scene is kept) never flashes it.
  // A fresh 3D build says so ('building') and is shown at once: it holds the page for ~1 s and the timer could not fire.
  const [loaderDue, setLoaderDue] = useState(false);
  useEffect(() => {
    setLoaderGone(false);
    setLoaderDue(false);
    const t = setTimeout(() => setLoaderDue(true), 200);
    return () => clearTimeout(t);
  }, [eventId]);
  useEffect(() => {
    if (!loaderHoldMs) return undefined;
    const t = setTimeout(() => setLoaderHeld(false), loaderHoldMs);
    return () => clearTimeout(t);
  }, [loaderHoldMs]);
  const onLoadStage = useCallback(
    (s) =>
      setMapStage((cur) => ({
        ...(cur.eventId === eventIdRef.current ? cur : { mounted: false, ready: false }),
        eventId: eventIdRef.current,
        [s]: true,
      })),
    [],
  );
  const mapStep = !mapPayload ? 0 : !mapStage.mounted ? 1 : !mapStage.ready ? 2 : 3;
  // Once its first frame is up, this map is on screen: a newer copy downloaded now waits until you leave it (festivalOffline).
  useEffect(() => {
    if (!mapStage.ready || !eventId) return undefined;
    setMapOnScreen(eventId);
    return () => setMapOnScreen(null);
  }, [mapStage.ready, eventId]);
  const loaderShownRef = useRef(null); // the map whose loader is on screen: it finishes (the dot) though the map is now ready
  // A 3D map (or one not loaded yet), still loading after the moment above - or already showing, so it can finish.
  const showMapLoader =
    Boolean(mapLoaderVariant) &&
    mapLoaderVariant !== 'off' &&
    !loaderGone &&
    (loaderDue || loaderHoldMs > 0 || mapStage.building) &&
    (mapStep < 3 || loaderShownRef.current === eventId) &&
    (!mapPayload || Boolean(mapPayload?.properties?.terrain?.scene3d));
  if (showMapLoader) loaderShownRef.current = eventId;
  // QA (Sam, 2026-10-22: the loader "starts again at the top petal"): a loader shown twice for one map restarts its lap.
  const loaderShowsRef = useRef({ eventId: null, n: 0 });
  useEffect(() => {
    if (!showMapLoader) return;
    const c = loaderShowsRef.current;
    if (c.eventId !== eventId) {
      c.eventId = eventId;
      c.n = 0;
    }
    c.n += 1;
    if (c.n > 1)
      appLog('warn', 'map.loader_restart', {
        eventId,
        shows: c.n,
        step: mapStep,
        hasPayload: Boolean(mapPayload),
        scene3d: Boolean(mapPayload?.properties?.terrain?.scene3d),
      });
  }, [showMapLoader]); // once per showing; the rest is read as it is then
  const { data: lineup } = useEventLineup(eventId, { enabled: Boolean(mapPayload) });
  // Without a Find session on this festival (going solo, or grouped for another event) the map shows
  // you from its own location stream - independent of Find, never published (operator, 2026-09-25).
  // The event row is not part of a festival download, so with no signal it may be missing; the
  // lineup is, and a set today (or last night, past midnight) says the festival is on.
  const lineupToday = useMemo(() => {
    const days = new Set([localDay(0), localDay(1)]);
    return (lineup ?? []).some(
      (r) => r?.performance_day && days.has(String(r.performance_day).slice(0, 10)),
    );
  }, [lineup]);
  // GPS warms up while the map data loads, not after it (operator, 2026-10-04: the avatar came well after the map) - but
  // only while the event is on: 3 h before to 3 h after (isInPresenceWindow). Browsing a map on any other day uses no
  // location and puts up no blue arrow (Sam, 2026-10-12). Offline with no event row, a set today says it is on.
  const mapEventOn = event ? isInPresenceWindow(event) : lineupToday;
  const soloSelf = useMapSelfLocation({ enabled: !live && mapEventOn, mayPrompt: mapEventOn });
  const venue = Array.isArray(event?.venues) ? event?.venues[0] : event?.venues;
  // One title rule for every map (lib/map/mapTitle): a festival's name over venue · city; a venue map's name over its city.
  const { title: titleName, subtitle: titleSubtitle } = mapTitle({
    event,
    venue,
    venueMap: Boolean(mapPayload?.properties?.venue_key),
  });

  const [selectedFeature, setSelectedFeature] = useState(null);
  // A selected stage shows its sets; the clock under them ticks while it is open, so "on now" moves on.
  const [stageNowMs, setStageNowMs] = useState(() => Date.now());
  const stageSelected = selectedFeature?.kind === 'stage';
  useEffect(() => {
    if (!stageSelected) return undefined;
    setStageNowMs(Date.now());
    const t = window.setInterval(() => setStageNowMs(Date.now()), 30000);
    return () => window.clearInterval(t);
  }, [stageSelected]);
  // A map opens in 3D (operator, 2026-09-23); a 2D/3D switch is remembered for that festival until the app closes,
  // so leaving and coming back keeps the view you had.
  const [viewMode, setViewModeState] = useState(() => viewModeByEvent.get(eventId) ?? '3d');
  const setViewMode = useCallback(
    (mode) => {
      viewModeByEvent.set(eventId, mode);
      setViewModeState(mode);
    },
    [eventId],
  );
  const [query, setQuery] = useState('');
  const [focusFeatureId, setFocusFeatureId] = useState(null);
  // Bumped on every pick, so picking the same result twice flies there twice.
  const [focusNonce, setFocusNonce] = useState(0);
  // A person picked from search or tapped: { peerId, peer }. WHERE they are is never stored -
  // it is read live every render, so the row, the ring and Go follow them as they move.
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [focusPoint, setFocusPoint] = useState(null);
  const [focusSection, setFocusSection] = useState(null);
  // A venue's seating sections by label (F2, Terrace 3, Garden 1 ...): they live inside the 3D scene's seating feature,
  // not in the map's features, so search reads them from the scene (Sam, 2026-10-05).
  const sectionNames = useMemo(() => {
    const out = [];
    for (const f of mapPayload?.properties?.terrain?.scene3d?.features ?? []) {
      if (f.type !== 'seating') continue;
      for (const s of f.sections ?? [])
        if (s.poly && s.name && !out.includes(s.name)) out.push(s.name);
    }
    return out;
  }, [mapPayload]);
  // Everything you can tap in the 3D scene that the map's feature list does not carry - The Cabin, the Ferris wheel,
  // Spectra, a lounge tent - is searchable too (Sam, 2026-10-06). Scenery stays out: trees, OSM pools and fountains,
  // roads, the grounds, and the amenity discs (those are the feature list's own rows).
  const sceneFinds = useMemo(() => {
    const out = [];
    for (const f of mapPayload?.properties?.terrain?.scene3d?.features ?? []) {
      if (f.pick === false || !f.name || SCENE_UNSEARCHABLE.has(f.type)) continue;
      // A building with several amenities is one result per amenity, by the title that amenity had before it joined the
      // building, else the building's name (Sam, 2026-10-22: "search for specific titles those amenities had prior").
      const ks =
        f.type === 'building' && Array.isArray(f.kinds)
          ? f.kinds.filter((k) => KIND_DISPLAY[k])
          : [];
      if (ks.length > 1) {
        ks.forEach((k, i) =>
          out.push({
            id: `scene-${f.id}:${i}`,
            sceneId: f.id,
            kind: k,
            name: f.kindNames?.[slotKey(ks, i)] || f.std || f.name,
            scene: true,
            slice: true,
            building: f.std || f.name,
          }),
        );
        continue;
      }
      out.push({
        id: `scene-${f.id}`,
        sceneId: f.id,
        kind: f.kind || SCENE_KIND[f.type] || 'other',
        name: f.std || f.name,
        scene: true,
      });
    }
    return out;
  }, [mapPayload]);
  // Guidance: the thing you pressed Go on. Not a route - see setGuide in priiScene.
  // { key, name, lat, lng, extentM } for a place; { key, name, peerId, lat, lng } for a person,
  // whose lat/lng is only the fallback - the live position leads.
  // A Go you left mid-way comes back with the map's view if you are back within 15 minutes (lib/map/mapReturn). Read as
  // the page first draws, so the map sees it as it opens; the map drops a Go-shaped view that came back without its Go.
  const [guide, setGuide] = useState(() => leftAsItWas(eventId, null)?.guide ?? null);
  const [recenterNonce] = useState(0);
  const guideRef = useRef(guide);
  guideRef.current = guide;
  const guideEventRef = useRef(eventId);
  useEffect(() => {
    setSelectedFeature(null);
    setSelectedPerson(null);
    setFocusPoint(null);
    setQuery('');
    setFocusFeatureId(null);
    if (guideEventRef.current !== eventId) {
      guideEventRef.current = eventId;
      setGuide(leftAsItWas(eventId, null)?.guide ?? null);
    }
    return () => rememberLeave(eventId, undefined, { guide: guideRef.current });
  }, [eventId]);

  const model = useMemo(() => parseFestivalMap(mapPayload), [mapPayload]);
  const venueBox = useMemo(() => (model ? venueBounds(model) : null), [model]);
  const scenarioFriend = scenarioFriendNameFromSearch(location.search);
  const play = useMemo(() => {
    if (!playMode || !venueBox) return null;
    return createPlayground({ bounds: venueBox });
  }, [playMode, venueBox]);
  useEffect(() => {
    if (!play) return undefined;
    play.start();
    window.__play = play;
    return () => {
      play.stop();
      if (window.__play === play) delete window.__play;
    };
  }, [play]); // __play: scripted QA (dev only)
  // Re-render on the playground's beat (once a second, and while you turn) so rows and heading reach the map.
  useSyncExternalStore(play ? play.subscribe : noSubscribe, () => (play ? play.version : 0));
  const scenarioSim = useMemo(
    () =>
      scenarioName && venueBox
        ? buildMapScenario(scenarioName, venueBox, Date.now(), scenarioFriend)
        : null,
    [scenarioName, venueBox, scenarioFriend],
  );
  const sim = play ? play.sim : scenarioSim;
  const simRef = useRef(sim);
  simRef.current = sim;
  const peerRows = sim ? sim.peers : proximity.peers;
  const getGpsPeers = sim ? () => sim.gpsPeers : proximity.getMergedGpsPeers;
  // The welcome flow's map turns with the phone's own compass (useDeviceHeading); other scenarios keep their fixed heading.
  const welcomeHeading = useDeviceHeading(sim?.name === 'welcome');
  const headingDegNow = sim ? (welcomeHeading ?? sim.headingDeg) : proximity.deviceHeadingDeg;
  const trackedPeerId = sim ? sim.trackedPeerId : proximity.trackedPeerId;

  // Search is a filter over the map's own named features — the venue's
  // vocabulary, not a geocoder.
  // Nine rows called "Restrooms" are one answer: the nearest one (to you, when you are on the map), with a count. The
  // 3D map then flies to the nearest scene feature of that name (operator, 2026-09-19).
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !model) return [];
    const me = live ? proximity.getSelfPosition?.() : soloSelf.getSelfPosition();
    const dist = (f) =>
      me?.lat != null && f.anchorLat != null
        ? Math.hypot(
            (f.anchorLng - me.lng) * Math.cos((me.lat * Math.PI) / 180),
            f.anchorLat - me.lat,
          )
        : 0;
    // A split building is answered by its slices (sceneFinds), not by its one database row.
    const splitNames = new Set(
      sceneFinds.filter((f) => f.slice).map((f) => f.building.toLowerCase()),
    );
    const hits = model.features.filter((f) => {
      if (f.name && splitNames.has(f.name.toLowerCase())) return false;
      const label = KIND_DISPLAY[f.kind]?.label ?? '';
      return (f.name && f.name.toLowerCase().includes(q)) || label.toLowerCase().includes(q);
    });
    const groups = new Map();
    for (const f of hits) {
      const key = `${f.kind}|${featureDisplayName(f).toLowerCase()}`;
      const g = groups.get(key);
      if (!g) groups.set(key, { f, n: 1 });
      else {
        g.n += 1;
        if (dist(f) < dist(g.f)) g.f = f;
      }
    }
    const named = new Set(hits.map((f) => (f.name || '').toLowerCase()));
    for (const f of sceneFinds) {
      const label = KIND_DISPLAY[f.kind]?.label ?? '';
      if (
        (!f.slice && named.has(f.name.toLowerCase())) ||
        !(
          f.name.toLowerCase().includes(q) ||
          label.toLowerCase().includes(q) ||
          (f.slice && f.building.toLowerCase().includes(q))
        )
      )
        continue;
      const key = `${f.kind}|${f.name.toLowerCase()}`;
      const g = groups.get(key);
      if (!g) groups.set(key, { f, n: 1 });
      else g.n += 1;
    }
    const places = [...groups.values()].map(({ f, n }) => ({ ...f, count: n })).slice(0, 6);
    const sections = sectionNames
      .filter((n) => n.toLowerCase().includes(q))
      .sort((a, b) =>
        a.toLowerCase() === q
          ? -1
          : b.toLowerCase() === q
            ? 1
            : a.localeCompare(b, undefined, { numeric: true }),
      )
      .slice(0, 6)
      .map((name) => ({ id: `section-${name}`, kind: 'other', name, section: true, count: 1 }));
    return [...places, ...sections];
  }, [query, model, live, proximity, sectionNames, sceneFinds]);

  // People: the subscribed group's members, only while this is the live map. A person off the venue (buildPeerPositions
  // gives them no position) can be found and named, but there is no puck and no camera move — the map never says where
  // someone is when they are not at the festival.
  const peopleResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !live || !model) return [];
    // The rows the map draws (peerRows): the live session's, or a dev scenario's / the playground's stand-ins.
    return (peerRows ?? [])
      .filter((p) => (p.displayName || '').toLowerCase().includes(q))
      .slice(0, 4);
  }, [query, live, model, peerRows]);
  const presenceOf = useCallback(
    (peer) =>
      peer?.display?.listPreview ??
      (peer?.display?.kind && peer.display.kind !== 'none' ? 'Sharing' : 'Not sharing'),
    [],
  );

  const pickResult = useCallback((feature) => {
    setSelectedPerson(null);
    if (feature.section) {
      setSelectedFeature(null);
      setFocusSection({ name: feature.name, nonce: Date.now() });
      setQuery('');
      return;
    }
    setFocusFeatureId(feature.id);
    setFocusNonce((n) => n + 1);
    setQuery('');
  }, []);
  const pickPerson = useCallback(
    (peer) => {
      const positions = buildPeerPositions({
        peerRows: [peer],
        gpsPeers: getGpsPeers?.() ?? [],
        selfPosition:
          (simRef.current ? simRef.current.self : proximity.getSelfPosition?.()) ?? null,
        bounds: model ? venueBounds(model) : null,
      }); // [[w,s],[e,n]], not model.bounds' {west,...}
      const pos = positions.find((p) => p.peerId === peer.peerId) ?? null;
      setSelectedFeature(null);
      setSelectedPerson({ peerId: peer.peerId, peer });
      // Finding someone by name is tracking them: UWB ranging starts now, not on a second tap (2026-10-04).
      if (!simRef.current && proximity.trackedPeerId !== peer.peerId)
        proximity.toggleTrackPeer(peer.peerId);
      if (pos) setFocusPoint({ lat: pos.lat, lng: pos.lng, nonce: Date.now() });
      setQuery('');
    },
    [proximity, model, getGpsPeers],
  );

  // Tapping a person behaves like tapping anything else on the map: it SELECTS them and the
  // row at the bottom says who they are. Before this it only toggled tracking, so a tap on a
  // face did nothing you could see (operator, 2026-09-24).
  const handlePeerTap = useCallback(
    (peerId) => {
      const peer = (peerRows ?? []).find((p) => p.peerId === peerId);
      if (peer) {
        // Where they are is read live in render (livePeerPos), never captured here. A tap tracks; it never
        // untracks the friend Go or a search just started ranging (2026-10-04 audit) - tapping away deselects.
        setSelectedFeature(null);
        setSelectedPerson({ peerId, peer });
        if (proximity.trackedPeerId !== peerId) proximity.toggleTrackPeer(peerId);
        return;
      }
      if (proximity.trackedPeerId !== peerId) proximity.toggleTrackPeer(peerId);
    },
    [proximity, peerRows],
  );

  // Close by: a friend the close-range stack (Bluetooth / UWB) can see, or on GPS inside Find's range. It lights the
  // corner's Find (Sam, 2026-10-13); the "close by · Track" row it used to show was a third button style and is gone.
  const closeBy = useMemo(() => {
    if (!live || !NATIVE_OR_SIM) return [];
    return (peerRows ?? [])
      .filter((p) => {
        const d = p.display;
        if (!d) return false;
        if (d.kind === 'uwb-live' || d.kind === 'ble-in-range') return true;
        return (
          d.kind === 'gps-live' && d.distanceMeters != null && d.distanceMeters <= FIND_OFFER_M
        );
      })
      .sort(
        (a, b) =>
          a.display.sortTier - b.display.sortTier ||
          a.display.sortDistance - b.display.sortDistance,
      );
  }, [live, peerRows]);

  // One source of "where am I", so the row, the Go button and the map all agree. Stable on
  // purpose: the map polls it, and a new identity on every fix or compass tick (it used to
  // depend on the whole proximity object) re-ran the map's effects that often - which is what
  // kept flying the camera back to a search result (2026-09-25 audit).
  const selfSrcRef = useRef(null);
  selfSrcRef.current = { live, proximity, soloSelf };
  const getSelfPos = useCallback(() => {
    if (simRef.current) return simRef.current.self;
    const src = selfSrcRef.current;
    // Live: the Find engine's fix, or the map's own last one until the engine has its first.
    return src.live
      ? (src.proximity.getSelfPosition?.() ?? src.soloSelf.getSelfPosition())
      : src.soloSelf.getSelfPosition();
  }, []);
  const selfPos = getSelfPos();
  /** How finely a distance to a friend can be said: a UWB range to the foot, GPS no finer than both fixes'
   *  error - inside it they read "Right here", as Find does (Greek, 2026-10-11: "20 ft" side by side). */
  const personDistanceOpts = (pos, peerId) =>
    pos?.source === 'uwb'
      ? { peerId, uwbPrecise: true }
      : {
          peerId,
          horizontalAccuracyMeters: combineHorizontalAccuracyMeters(
            selfPos?.accuracyMeters,
            pos?.accuracyMeters,
          ),
        };

  // While someone is selected or guidance runs, re-read positions every couple of seconds:
  // a friend's GPS can move without anything else re-rendering this page.
  const [liveTick, setLiveTick] = useState(0);
  const needLiveTick = Boolean(selectedPerson || guide);
  useEffect(() => {
    if (!needLiveTick) return undefined;
    const id = window.setInterval(() => setLiveTick((n) => n + 1), LIVE_TICK_MS);
    return () => window.clearInterval(id);
  }, [needLiveTick]);

  /** At the festival: inside the venue plus the same ~250 m margin the 3D map clamps you to.
   *  Go is only offered here. From LA on the Portola map it flew the camera 500 km off the
   *  scene and the map went black (operator, 2026-09-25). */
  const onSite = Boolean(
    selfPos?.lat != null &&
    venueBox &&
    selfPos.lng >= venueBox[0][0] - VENUE_MARGIN_DEG &&
    selfPos.lng <= venueBox[1][0] + VENUE_MARGIN_DEG &&
    selfPos.lat >= venueBox[0][1] - VENUE_MARGIN_DEG &&
    selfPos.lat <= venueBox[1][1] + VENUE_MARGIN_DEG,
  );
  /** A friend's position right now, or null when they cannot be placed (off the venue, not
   *  sharing, or last seen too long ago). Declared here, after selfPos and venueBox, on purpose. */
  const livePeerPos = (peerId) => {
    if (!live || !peerId) return null;
    const peer = (peerRows ?? []).find((p) => p.peerId === peerId);
    if (!peer) return null;
    const pos =
      buildPeerPositions({
        peerRows: [peer],
        gpsPeers: getGpsPeers?.() ?? [],
        selfPosition: selfPos,
        bounds: venueBox,
      }).find((x) => x.peerId === peerId) ?? null;
    return pos && !isPositionExpired(pos) ? pos : null;
  };
  const personPeer = selectedPerson
    ? ((peerRows ?? []).find((p) => p.peerId === selectedPerson.peerId) ?? selectedPerson.peer)
    : null;
  const personPos = selectedPerson ? livePeerPos(selectedPerson.peerId) : null;
  const personTone = personPos ? positionAgeTone(personPos) : null;
  const ageText = (p) =>
    p.fused || !Number.isFinite(p.lastKnownAt) ? 'now' : formatStalenessShort(p.lastKnownAt);

  // Where the arrow points. A person is followed live; if they drop off the map mid-walk, the
  // arrow keeps their last position rather than jumping back to where Go was pressed.
  const guidePeerPos = guide?.peerId ? livePeerPos(guide.peerId) : null;
  const guidePeerLastRef = useRef(null); // { key, lat, lng, lastKnownAt, fused }, for THIS guide only
  // Heard over Bluetooth but their GPS is stale (airplane mode beside you): the last spot is somewhere they no longer
  // are, so Go stops pointing there and reads Find's "nearby" band instead (2026-10-16 audit).
  const guidedRow = guide?.peerId
    ? ((peerRows ?? []).find((p) => p.peerId === guide.peerId) ?? null)
    : null;
  const guidedByBluetooth = guidedRow?.display?.kind === 'ble-in-range';
  if (guidedByBluetooth) guidePeerLastRef.current = null;
  if (guidePeerPos)
    guidePeerLastRef.current = {
      key: guide.key,
      lat: guidePeerPos.lat,
      lng: guidePeerPos.lng,
      lastKnownAt: guidePeerPos.lastKnownAt,
      fused: guidePeerPos.fused,
      arrowMinM: guidePeerPos.arrowMinM ?? null,
      arriveM: guidePeerPos.arriveM ?? null,
      distanceM: guidePeerPos.distanceM ?? null,
      source: guidePeerPos.source ?? null,
      accuracyMeters: guidePeerPos.accuracyMeters ?? null,
    };
  const guidePeerLast =
    guide?.peerId && guidePeerLastRef.current?.key === guide.key ? guidePeerLastRef.current : null;
  const guideAtLat = guide ? (guidePeerLast?.lat ?? guide.lat) : null;
  const guideAtLng = guide ? (guidePeerLast?.lng ?? guide.lng) : null;
  const guideArrowMinM = guidePeerLast?.arrowMinM ?? null;
  const guideTo = useMemo(
    () =>
      guide && !guidedByBluetooth && guideAtLat != null
        ? {
            key: guide.key,
            name: guide.name,
            lat: guideAtLat,
            lng: guideAtLng,
            minArrowM: guideArrowMinM,
            person: Boolean(guide.peerId),
            peerId: guide.peerId ?? null,
          }
        : null,
    [guide, guidedByBluetooth, guideAtLat, guideAtLng, guideArrowMinM],
  );
  const guideTargetTone = guidePeerLast ? positionAgeTone(guidePeerLast) : null;

  // Leave the venue mid-guidance and the guidance ends rather than pointing across the state -
  // but only once you have been off site for a while. At a gate a single bad fix lands outside.
  const offSiteSinceRef = useRef(null);
  useEffect(() => {
    if (!guide || selfPos?.lat == null || onSite) {
      offSiteSinceRef.current = null;
      return;
    }
    const now = Date.now();
    if (offSiteSinceRef.current == null) offSiteSinceRef.current = now;
    else if (now - offSiteSinceRef.current >= OFF_SITE_HOLD_MS) {
      offSiteSinceRef.current = null;
      setGuide(null);
    }
  }, [guide, onSite, selfPos?.lat, liveTick]);

  // The guidance row's number. Removing the QA harness (6c5f397) took this line with it, and
  // the row then threw a ReferenceError on every Go - the map went black (2026-09-25).
  const guideMeters = guidedByBluetooth
    ? (guidedRow.display.distanceMeters ?? null)
    : guideTo && selfPos
      ? (guidePeerLast?.distanceM ??
        haversineMeters(selfPos.lat, selfPos.lng, guideTo.lat, guideTo.lng))
      : null;
  // Find inside Maps. Lit while walking to someone inside Find's range (kept to FIND_KEEP_M so it does not flicker),
  // or, browsing, while a friend is close by.
  const [guideFindOn, setGuideFindOn] = useState(false);
  useEffect(() => {
    setGuideFindOn((was) => (guide?.peerId ? findOffered(guideMeters, was) : false));
  }, [guide?.peerId, guideMeters]);
  const findLit = guide ? guideFindOn : closeBy.length > 0;
  // Which way to turn, relative to where the phone faces: the directions bar's arrow points at the
  // target. With the map turned to your heading, the arrow on the ground is edge-on to a camera
  // this low and cannot say "it's to your left" (2026-09-25); this can. Null without a compass.
  const headingNow = (live ? headingDegNow : soloSelf.headingDeg) ?? null;
  const guideTurnRaw =
    guideTo && selfPos && headingNow != null
      ? (bearingDeg(selfPos, guideTo) - headingNow + 360) % 360
      : null;
  // Unwrapped, so the CSS transition always turns the short way: 350 -> 10 is +20, not -340.
  const guideTurnRef = useRef(null);
  if (guideTurnRaw == null) guideTurnRef.current = null;
  else if (guideTurnRef.current == null) guideTurnRef.current = guideTurnRaw;
  else guideTurnRef.current += ((((guideTurnRaw - guideTurnRef.current) % 360) + 540) % 360) - 180;
  const guideTurnDeg = guideTurnRef.current;
  // Arriving ends it. Being made to dismiss guidance for a stage you are standing at is the
  // app failing to notice something you already know (operator, 2026-09-24). Two ways in:
  // inside the radius for a few seconds, or stopped close by for most of a minute.
  const arriveSinceRef = useRef(null);
  const dwellRef = useRef(null); // { lat, lng, since }: where you stopped, and when
  useEffect(() => {
    arriveSinceRef.current = null;
    dwellRef.current = null;
  }, [guide?.key]);
  useEffect(() => {
    if (!guide || guideMeters == null || !selfPos) return;
    const now = Date.now();
    const radius =
      guide.peerId && guidePeerLast?.arriveM != null ? guidePeerLast.arriveM : arriveRadius(guide);
    if (guideMeters <= radius) {
      if (arriveSinceRef.current == null) arriveSinceRef.current = now;
      else if (now - arriveSinceRef.current >= ARRIVE_HOLD_MS) {
        setGuide(null);
        return;
      }
    } else arriveSinceRef.current = null;
    if (guideMeters <= radius + DWELL_NEAR_M) {
      const d = dwellRef.current;
      if (!d || haversineMeters(d.lat, d.lng, selfPos.lat, selfPos.lng) > DWELL_STILL_M)
        dwellRef.current = { lat: selfPos.lat, lng: selfPos.lng, since: now };
      else if (now - d.since >= DWELL_MS) {
        setGuide(null);
      }
    } else dwellRef.current = null;
  }, [guide, guideMeters, selfPos, liveTick]);

  // Opening a map is saying where you are: if one of your live groups is for this event and the session is on
  // another, switch to it - a friend who joined this weekend's group while last month's was selected saw nobody
  // here (2026-10-04 audit). The Find page's picker still rules after that.
  useEffect(() => {
    if (!eventId || live || !proximity.setSelectedGroupId) return;
    const mine = (proximity.groups ?? []).find(
      (g) => !g.isPast && (g.eventId === eventId || (g.eventRootId ?? g.eventId) === mapRootId),
    );
    if (mine && mine.id !== proximity.selectedGroupId)
      proximity.setSelectedGroupId(mine.id, { source: 'map' });
  }, [
    eventId,
    live,
    mapRootId,
    proximity.groups,
    proximity.selectedGroupId,
    proximity.setSelectedGroupId,
  ]);

  // Scenario mode: the friend is selected (and tracked) on load, and guided to when the scenario says so.
  const simGuideArmedRef = useRef(null);
  useEffect(() => {
    if (!sim) return;
    const row = sim.peers.find((p) => p.peerId === sim.selectPeerId);
    if (!row) return undefined;
    const select = () => {
      setSelectedFeature(null);
      setSelectedPerson({ peerId: row.peerId, peer: row });
    };
    select();
    // The 3D scene clears the selection once it has loaded (onFeatureSelect(null)), which undid this; select again after.
    const t = window.setTimeout(select, 2500);
    return () => window.clearTimeout(t);
  }, [sim]);
  useEffect(() => {
    if (!sim?.guide || !personPos || simGuideArmedRef.current === sim.name) return;
    simGuideArmedRef.current = sim.name;
    setGuide({
      key: `peer:${sim.selectPeerId}`,
      peerId: sim.selectPeerId,
      lat: personPos.lat,
      lng: personPos.lng,
      name: personPeer?.displayName ?? 'Friend',
    });
  }, [sim, personPos, personPeer]);

  const { profile: viewerProfile } = useCurrentUserProfile();
  const selfProfile = useMemo(
    () =>
      viewerProfile
        ? { displayName: viewerProfile.display_name, photoUrl: viewerProfile.profile_photo_url }
        : null,
    [viewerProfile],
  );

  /** How far the selected thing is, in metres - the number Go is gated on. */
  const selectedMeters =
    selectedFeature && selfPos && selectedFeature.anchorLat != null
      ? haversineMeters(
          selfPos.lat,
          selfPos.lng,
          selectedFeature.anchorLat,
          selectedFeature.anchorLng,
        )
      : null;
  const personMeters =
    personPos && selfPos && onSite
      ? (personPos.distanceM ??
        haversineMeters(selfPos.lat, selfPos.lng, personPos.lat, personPos.lng))
      : null;
  /** Offering Go for something you are already standing at is a loop: press it, arrive
   *  immediately, watch it dismiss itself (operator, 2026-09-24). Same threshold that ends
   *  guidance, so the two can never disagree. A person's radius is the placement's (UWB: 2 m). */
  const worthWalkingTo = (m, target) =>
    onSite &&
    m != null &&
    m > (target?.peerId && personPos?.arriveM != null ? personPos.arriveM : arriveRadius(target));

  const selectedDistance =
    selectedFeature && selfPos && onSite
      ? formatFindDistance(
          haversineMeters(
            selfPos.lat,
            selfPos.lng,
            selectedFeature.anchorLat,
            selectedFeature.anchorLng,
          ),
        )
      : null;

  // Precise Location off (iOS "approximate"): you cannot be placed on the grounds.
  // Precise Location off: the solo stream says so itself; in a live session it shows as a fix a kilometre wide
  // that never lands on the venue (2026-10-04 audit: the row was silent then).
  const liveFixWide = live && (proximity.getSelfPosition?.()?.accuracyMeters ?? 0) >= 1000;
  const preciseOff = (!live && soloSelf.approximate && !selfPos) || (liveFixWide && !onSite);
  // Location / Precise / Bluetooth at a glance (the Permissions B pick, 2026-10-04). Native only; ?perms= in dev.
  const permRows = permissionRows({
    locationAuth: soloSelf.denied ? 'denied' : proximity.locationAuth,
    locationLost: proximity.locationAuthLost === true,
    preciseOff,
    bleState: proximity.bleState,
  });
  const showPermStatus =
    NATIVE_OR_SIM ||
    Boolean(import.meta.env.DEV && new URLSearchParams(location.search).get('perms'));

  // The 2D map's ring follows the 3D rule (2026-10-12): the person you selected or are walking to, never a tracking left
  // behind after you tapped away.
  const ringPeerId = live ? (guide?.peerId ?? selectedPerson?.peerId ?? null) : null;

  const mapProps = {
    fullScreen: true,
    mapPayload,
    peers: live ? peerRows : NO_ROWS,
    lineup: lineup ?? NO_ROWS,
    getSelfPosition: getSelfPos,
    selfFixAt: soloSelf.fixAt,
    getMergedGpsPeers: live ? getGpsPeers : getNoPeers,
    deviceHeadingDeg: live ? headingDegNow : soloSelf.headingDeg,
    trackedPeerId: live ? trackedPeerId : null,
    onPeerTap: handlePeerTap,
    // A tap on empty ground clears whatever was selected - person included. Before this a
    // person could only be dismissed by selecting something else (operator, 2026-09-24).
    onFeatureSelect: (f) => {
      setSelectedFeature(f);
      setSelectedPerson(null);
    },
    focusFeatureId,
    focusPoint,
    focusSection,
    titleSubtitle,
    titleName,
    viewMode,
    onViewModeChange: setViewMode,
    selfProfile,
    selectedPeerId: selectedPerson?.peer?.peerId ?? null,
    selectedPeerAt: personPos ? { lat: personPos.lat, lng: personPos.lng } : null,
    focusNonce,
    // Find is reachable from a map whose event your Find session is on, and only while you are on that map (Sam,
    // 2026-10-21: from home the button only opened a Find with no one to find). Dev scenarios and ?play keep it.
    canFind: live && (onSite || scenarioName != null || playMode),
    findLit: live && onSite && findLit,
    recenterNonce,
    openAt: sim?.openAt ?? null,
    guideTo,
    guideHeadingDeg: (live ? headingDegNow : soloSelf.headingDeg) ?? null,
  };

  // While you walk somewhere, tapping something else still offers it: its row comes up at the bottom (Go switches to it),
  // and the walk's own target gets no second row (Sam, 2026-10-26: mid-walk a tap showed nothing, only Re-center).
  const guidingTo = (key) => guide?.key === key;
  const featureRowShown = Boolean(selectedFeature) && !guidingTo(`feature:${selectedFeature?.id}`);
  const personRowShown =
    Boolean(selectedPerson && personPeer && !selectedFeature) &&
    !guidingTo(`peer:${selectedPerson?.peerId}`);

  return (
    <div
      className={`maps-page${showMapLoader ? ' maps-page--loading' : ''}${featureRowShown || personRowShown ? ' maps-page--row' : ''}`}
    >
      {/* The name only once the map's own data says which it is: before that it was the event's, then switched to the
          venue's mid-load (My Chemical Romance -> Hollywood Bowl, 2026-10-23). */}
      {showMapLoader && (
        <MapLoader
          key={eventId}
          variant={mapLoaderVariant}
          step={loaderHeld ? Math.min(mapStep, 2) : mapStep}
          name={mapPayload ? titleName || null : null}
          onDone={() => setLoaderGone(true)}
        />
      )}
      {mapPayload ? (
        <Suspense fallback={null}>
          {/* Editing lives in Studio (/studio), not here: this page only views. */}
          {mapPayload?.properties?.terrain?.scene3d ? (
            <FestivalMap3D {...mapProps} eventId={eventId} onLoadStage={onLoadStage} />
          ) : (
            <FestivalMap {...mapProps} trackedPeerId={ringPeerId} />
          )}
          {play && <MapPlayground play={play} />}
        </Suspense>
      ) : (
        <p className="maps-page__note">
          {isLoading ? null : 'No published map for this event yet.'}
        </p>
      )}

      {/* Searching is not what you are doing while walking somewhere: the pill and its chips
          stand down for the duration, and the readout takes the top of the screen. */}
      {!guide && (
        <div className="maps-page__top">
          <div className="maps-page__topbar">
            {/* Always here, however the map was reached (tab bar, event page, a link): it goes to the map list
                (operator, 2026-10-19). */}
            {/* Not in the welcome flow: its bar is the way on (lib/welcomeDemo). */}
            {!isWelcomeDemo(location.search) && (
              <button
                type="button"
                className="maps-page__back"
                aria-label="Back to your maps"
                onClick={() => goBackTo(navigate, '/map', { state: { stay: true } })}
              >
                <ChevronLeft size={18} aria-hidden="true" />
              </button>
            )}
            {/* Also while the map loads (disabled): the pill is what holds the status button at the right edge, and
                without it that button showed beside Back for a moment (Sam, 2026-10-21, the Greek). */}
            {(mapPayload || isLoading) && (
              <div className="maps-page__search">
                <Search size={15} aria-hidden="true" />
                <input
                  type="search"
                  disabled={!mapPayload}
                  value={query}
                  placeholder="Search this map"
                  aria-label="Search this map"
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query && (
                  <button type="button" aria-label="Clear search" onClick={() => setQuery('')}>
                    <X size={14} />
                  </button>
                )}
              </div>
            )}
            {showPermStatus && (
              <PermissionsStatus
                rows={permRows}
                onAskLocation={() => {
                  void proximity.requestLocationWhenInUse?.();
                }}
              />
            )}
          </div>
          {(results.length > 0 || peopleResults.length > 0) && (
            <ul className="maps-page__results">
              {results.length > 0 && peopleResults.length > 0 && (
                <li className="maps-page__results-label">Places</li>
              )}
              {results.map((f) => (
                <li key={f.id}>
                  <button type="button" onClick={() => pickResult(f)}>
                    <span
                      className="maps-page__result-icon"
                      style={{ color: KIND_DISPLAY[f.kind]?.color }}
                      dangerouslySetInnerHTML={{ __html: iconMarkup(f.kind, 16) }}
                    />
                    <span>{featureDisplayName(f, KIND_DISPLAY[f.kind]?.label)}</span>
                    <span className="maps-page__result-kind">
                      {f.section ? 'Section' : KIND_DISPLAY[f.kind]?.label}
                      {f.count > 1 ? ` · ${f.count}, nearest first` : ''}
                    </span>
                  </button>
                </li>
              ))}
              {peopleResults.length > 0 && <li className="maps-page__results-label">People</li>}
              {peopleResults.map((p) => (
                <li key={`p-${p.peerId}`}>
                  <button
                    type="button"
                    className="maps-page__result-person"
                    onClick={() => pickPerson(p)}
                  >
                    <Avatar displayName={p.displayName} profilePhotoUrl={p.photoUrl} size="xxs" />
                    <span>
                      <Highlight text={p.displayName} query={query} />
                    </span>
                    <span className="maps-page__result-kind">{presenceOf(p)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {personRowShown &&
        (() => {
          // One line: name, then distance and age without "ago", then a plain Go (Sam's pick, 2026-10-04, from six
          // layouts: the name gets the most room this way without a second line).
          const age = personPos ? ageText(personPos).replace(/ ago$/, '') : null;
          const dist =
            personMeters != null
              ? formatFindDistance(
                  personMeters,
                  personDistanceOpts(personPos, selectedPerson.peerId),
                )
              : null;
          return (
            <div className="maps-page__row">
              <Avatar
                displayName={personPeer.displayName}
                profilePhotoUrl={personPeer.photoUrl}
                size="xxs"
              />
              <span className="maps-page__row-name">{personPeer.displayName}</span>
              <span className="maps-page__row-dist">
                {/* Distance first, like an amenity - that is the question you are asking when you
                  tap someone (operator, 2026-09-24). Presence only when we cannot place them.
                  How old the position is rides beside it, coloured by trust (operator, 2026-09-25). */}
                {personPos ? (
                  <>
                    {dist && `${dist} · `}
                    <span className={`seen--${personTone}`}>{age}</span>
                  </>
                ) : (
                  (() => {
                    // Not on the map, but not unknown: a fresh fix past the map's edge (the lot, the campground) says how
                    // far; Bluetooth presence without a fix says nearby (2026-10-04 audit).
                    const raw =
                      (getGpsPeers?.() ?? []).find((p) => p.id === selectedPerson.peerId) ?? null;
                    if (raw && selfPos && !isPositionExpired({ lastKnownAt: raw.lastKnownAt })) {
                      return `Off the map · ${formatFindDistance(haversineMeters(selfPos.lat, selfPos.lng, raw.lat, raw.lng))}`;
                    }
                    return personPeer.display?.kind === 'ble-in-range'
                      ? bleNearbyLabel(personPeer.display.distanceMeters)
                      : 'Not at the venue';
                  })()
                )}
              </span>
              {/* Walking to a person is the same act as walking to a stage, so it is the same
                button in the same place (operator, 2026-09-24). */}
              {personPos && worthWalkingTo(personMeters, { peerId: selectedPerson.peerId }) && (
                <button
                  type="button"
                  className="maps-page__go"
                  onClick={() => {
                    // Walking to someone is tracking them: the close-range stack (UWB) starts with Go (2026-10-04).
                    if (!sim && proximity.trackedPeerId !== selectedPerson.peerId)
                      proximity.toggleTrackPeer(selectedPerson.peerId);
                    setGuide({
                      key: `peer:${selectedPerson.peerId}`,
                      peerId: selectedPerson.peerId,
                      lat: personPos.lat,
                      lng: personPos.lng,
                      name: personPeer.displayName,
                    });
                  }}
                >
                  Go
                </button>
              )}
            </div>
          );
        })()}
      {/* Guiding: the row becomes the instruction. Distance and ETA are live, so the number
          moving is the feedback that you are walking the right way. */}
      {/* Directions mode: the distance is the whole point, so it leads, at the top, in the
          size the radar readout uses - not tucked into the bottom-right of a row
          (operator, 2026-09-24). Same words as Find: formatFindDistanceWithWalkTime. */}
      {guide && (
        <div className="maps-page__guide">
          {/* lucide's Navigation arrow points up-right (45 deg), hence the -45. */}
          <span
            className="maps-page__guide-icon"
            aria-hidden="true"
            style={
              guideTurnDeg != null ? { transform: `rotate(${guideTurnDeg - 45}deg)` } : undefined
            }
          >
            <Navigation size={18} />
          </span>
          <span className="maps-page__guide-text">
            <span className="maps-page__guide-dist">
              {guidedByBluetooth
                ? bleNearbyLabel(guideMeters)
                : guideMeters == null
                  ? 'Looking for you…'
                  : formatFindDistanceWithWalkTime(
                      guideMeters,
                      guidePeerLast ? personDistanceOpts(guidePeerLast) : {},
                    )}
            </span>
            <span className="maps-page__guide-name">
              {guide.name}
              {guideTargetTone && guideTargetTone !== 'live' && (
                <span className={`seen--${guideTargetTone}`}>{` · ${ageText(guidePeerLast)}`}</span>
              )}
            </span>
          </span>
          <button
            type="button"
            className="maps-page__go maps-page__go--stop"
            onClick={() => setGuide(null)}
          >
            End
          </button>
        </div>
      )}

      {stageSelected && !guide && (
        <StageSetsPreview
          eventId={eventId}
          stage={selectedFeature.lineupStageName ?? selectedFeature.name}
          lineup={lineup}
          nowMs={stageNowMs}
        />
      )}
      {featureRowShown && (
        <div className="maps-page__row">
          <span
            className="maps-page__row-icon"
            style={{ color: KIND_DISPLAY[selectedFeature.kind]?.color }}
            dangerouslySetInnerHTML={{ __html: iconMarkup(selectedFeature.kind, 18) }}
          />
          <span className="maps-page__row-name">
            {featureDisplayName(selectedFeature, KIND_DISPLAY[selectedFeature.kind]?.label)}
          </span>
          {selectedDistance && <span className="maps-page__row-dist">{selectedDistance}</span>}
          {/* Go only when we know where you are - otherwise the arrow would point from nowhere. */}
          {worthWalkingTo(selectedMeters, selectedFeature) && (
            <button
              type="button"
              className="maps-page__go"
              onClick={() =>
                setGuide({
                  key: `feature:${selectedFeature.id}`,
                  lat: selectedFeature.anchorLat,
                  lng: selectedFeature.anchorLng,
                  extentM: selectedFeature.extentM ?? 0,
                  name: featureDisplayName(
                    selectedFeature,
                    KIND_DISPLAY[selectedFeature.kind]?.label,
                  ),
                })
              }
            >
              <Navigation size={14} aria-hidden="true" /> Go
            </button>
          )}
        </div>
      )}
      {/* Off site the map says nothing about where you are (operator, 2026-09-25): no "12 mi to the
          venue" row. You appear when you are on the map, and not before. Location / Precise Location off are
          the status button's amber dot, not a bar here (operator, 2026-10-04). */}
    </div>
  );
}
